import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Loading } from 'secondstep-components'
import { ASSIGNING_LICENSE_TEXT } from 'lib/constants'
import { operations as siteOperations } from 'store/sitesManager'
import { operations as licenseOperations } from 'store/licenseManager'
import { paths } from 'components/LUMRouter/paths'
import LicenseManagement from './component'

const transformNetSuiteAssetsToUserLicenses = assets => {
  if (!Array.isArray(assets)) {
    return {
      activeLicenses: [],
      unassignedLicenses: [],
      inactiveLicenses: [],
      adminLicenses: [],
    }
  }

  const now = new Date()
  const activeNetSuiteAssets = []
  const unassignedNetSuiteAssets = []
  const inactiveNetSuiteAssets = []

  assets.forEach(asset => {
    const expirationDate = asset.expirationDate
      ? new Date(asset.expirationDate)
      : null
    const isInactive =
      (expirationDate && expirationDate < now) || asset.isActive === false
    const isAssigned = asset.site && asset.site.id

    if (isInactive) {
      inactiveNetSuiteAssets.push(asset)
    } else if (!isAssigned) {
      unassignedNetSuiteAssets.push(asset)
    } else {
      activeNetSuiteAssets.push(asset)
    }
  })

  return {
    activeLicenses: activeNetSuiteAssets,
    unassignedLicenses: unassignedNetSuiteAssets,
    inactiveLicenses: inactiveNetSuiteAssets,
    adminLicenses: activeNetSuiteAssets,
  }
}

const LicenseManagementContainer = ({ pushTo }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const [selectedLicense, setSelectedLicense] = useState(null)

  const { currentUser, isUpdating, userLicenses } = useSelector(state => {
    const { isLoading, netSuiteAssets } = state.netSuiteAssetManager
    const { profile } = state.userProfileManager

    const userLicenses = transformNetSuiteAssetsToUserLicenses(netSuiteAssets)

    return {
      currentUser: profile,
      isLoading,
      isUpdating: false,
      netSuiteAssets,
      userLicenses,
    }
  })

  // Clear selectedLicense when navigating away from assign-license page
  useEffect(() => {
    if (location.pathname !== paths.ASSIGN_LICENSE && selectedLicense) {
      setSelectedLicense(null)
    }
  }, [location.pathname, selectedLicense])

  const onSelectLicense = selectedLicense => setSelectedLicense(selectedLicense)

  const clearSelectedLicense = () => {
    setSelectedLicense(null)
    dispatch(licenseOperations.fetchLicenses())
    dispatch(siteOperations.fetchSites())
    // Navigate to unassigned licenses by default, or keep current path if already on a list page
    if (
      location.pathname === paths.ASSIGN_LICENSE ||
      location.pathname === paths.BASE_LICENSE_MANAGEMENT
    ) {
      pushTo(paths.UNASSIGNED_LICENSES)
    } else {
      // If we're on a list page, stay on it
      pushTo(location.pathname)
    }
  }

  const onSelectUserManagement = siteId => pushTo(`/user-management/${siteId}`)

  const onSelectAssign = () => pushTo('/license-management/assign-license')

  return (
    <>
      {isUpdating && (
        <Loading
          dataTestId="saving-assigning-license"
          loadingText={ASSIGNING_LICENSE_TEXT}
        />
      )}
      {!isUpdating && (
        <LicenseManagement
          clearSelectedLicense={clearSelectedLicense}
          currentUser={currentUser}
          onSelectAssign={onSelectAssign}
          onSelectLicense={onSelectLicense}
          onSelectUserManagement={onSelectUserManagement}
          pushTo={pushTo}
          selectedLicense={selectedLicense}
          userLicenses={userLicenses}
        />
      )}
    </>
  )
}

LicenseManagementContainer.propTypes = {
  pushTo: PropTypes.func.isRequired,
}

export default LicenseManagementContainer
